import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BASE_CURRENCY, canApplyAction, hasAssignment } from "@torre/assets";

import { Badge, Button, Card, CardHeader, EmptyState, Table, type TableColumn } from "../../../components/ui";
import { confirmDialog } from "../../../components/ui/dialog";
import { AssetFormModal } from "../components/AssetFormModal";
import { AssignModal, CloseMaintenanceModal, OpenMaintenanceModal, RetireModal } from "../components/AssetActionModals";
import { AttachmentsPanel } from "../components/AttachmentsPanel";
import { ErrorState, InfoRow, InlineSpinner, Loader, SectionFrame } from "../components/shared";
import {
  useActivosMutation,
  useAsset,
  useAssetEvents,
  useAssetMaintenance,
  useBases,
  useCanSeeCosts,
  useSuppliers,
  useVehicles,
} from "../hooks/useActivos";
import { handleSaveError } from "../lib/conflicts";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import {
  ASSET_EVENT_ICONS,
  ASSET_STATUS_TONES,
  MAINTENANCE_STATUS_TONES,
  WARRANTY_TONES,
  categoryLabel,
  eventLabel,
  langOf,
  maintenanceKindLabel,
  maintenanceStatusLabel,
  retireReasonLabel,
  statusLabel,
  toneOf,
  type Lang,
} from "../lib/vocab";
import { ACTIVOS_BASE } from "../paths";
import { activosApi, type AssetEventRow, type MaintenanceRow } from "../services/activosApi";

// ============================================================================
// Detalle de un activo: ficha, compra y garantía, asignación, mantenimiento
// (abrir y cerrar), baja con motivo (no borra) y la línea de tiempo de
// `asset_events`. Archivo común, idéntico en CC-CONSOLE y WAYAK. Qué botones
// se ofrecen lo decide `canApplyAction` de @torre/assets, la misma regla que
// aplica el API.
// ============================================================================

type OpenModal = "edit" | "assign" | "maintenance" | "retire" | null;

/** Resumen de una línea de un evento de la línea de tiempo. */
function eventDetail(ev: AssetEventRow, lang: Lang, t: TFunction): string | null {
  const d = (ev.data || {}) as Record<string, unknown>;
  switch (ev.type) {
    case "edicion": {
      const changes = Object.keys((d.changes as Record<string, unknown>) || {});
      // `purchase.unitCost` → `timeline.fields.purchase_unitCost` (llaves planas).
      const labels = changes.map((f) => t(`timeline.fields.${f.replace(/\./g, "_")}`, { defaultValue: f }));
      return changes.length ? t("timeline.changed", { fields: [...new Set(labels)].join(", ") }) : null;
    }
    case "asignacion": {
      const a = (d.assignment || {}) as { custodian?: { name?: string | null; email?: string | null } | null; location?: string | null };
      return [a.custodian?.name || a.custodian?.email, a.location].filter(Boolean).join(" · ") || null;
    }
    case "baja":
      return [retireReasonLabel(d.reason as string, lang), d.note as string].filter(Boolean).join(" · ") || null;
    case "mantenimiento_abierto":
      return maintenanceKindLabel(d.kind as string, lang);
    case "mantenimiento_cerrado":
      return d.statusAfter ? t("timeline.backTo", { status: statusLabel(d.statusAfter as string, lang) }) : null;
    default:
      return null;
  }
}

export default function ActivoDetalleSection() {
  const { assetId } = useParams<{ assetId: string }>();
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const canSeeCosts = useCanSeeCosts();
  const asset = useAsset(assetId);
  const events = useAssetEvents(assetId);
  const maintenance = useAssetMaintenance(assetId);
  const suppliers = useSuppliers({});
  const vehicles = useVehicles(Boolean(asset.data?.assignment?.vehicleId));
  const bases = useBases(Boolean(asset.data?.assignment?.baseId));
  const [modal, setModal] = useState<OpenModal>(null);
  const [closing, setClosing] = useState<MaintenanceRow | null>(null);
  const giveBack = useActivosMutation((id: string) => activosApi.returnToStock(id));

  const supplierName = (id: string | null | undefined) => (id ? suppliers.data?.items.find((s) => s.id === id)?.name || "—" : "—");
  const timeline = useMemo(() => (events.data?.pages || []).flatMap((p) => p.items || []), [events.data]);
  const maintenances = maintenance.data?.items || [];
  const openMaintenance = maintenances.find((m) => m.status === "abierto") || null;

  const back = (
    <Button size="sm" variant="ghost" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate(`${ACTIVOS_BASE}/inventario`)}>
      {t("detail.back")}
    </Button>
  );

  if (asset.isLoading) {
    return (
      <SectionFrame title={t("detail.loading")} actions={back}>
        <Loader />
      </SectionFrame>
    );
  }
  if (asset.isError || !asset.data) {
    return (
      <SectionFrame title={t("detail.title")} actions={back}>
        <ErrorState error={asset.error} onRetry={() => void asset.refetch()} />
      </SectionFrame>
    );
  }

  const a = asset.data;
  const p = a.purchase;
  const status = a.status;
  const can = (action: Parameters<typeof canApplyAction>[1]) => canApplyAction(status, action);
  const warranty = a.computed?.warranty?.status || "sin_garantia";
  const vehicleName = a.assignment?.vehicleId ? vehicles.data?.find((v) => v.id === a.assignment.vehicleId)?.name || a.assignment.vehicleId : null;
  const baseName = a.assignment?.baseId ? bases.data?.find((b) => b.id === a.assignment.baseId)?.name || a.assignment.baseId : null;

  const onReturn = async () => {
    const ok = await confirmDialog({ title: t("assign.returnTitle"), text: t("assign.returnText"), confirmText: t("assign.return"), cancelText: t("common.cancel") });
    if (!ok) return;
    try {
      await giveBack.mutateAsync(a.id);
      toast.success(t("assign.returned"));
    } catch (err) {
      await handleSaveError(err, t, { navigate });
    }
  };

  const maintenanceColumns: TableColumn<MaintenanceRow>[] = [
    { key: "kind", header: t("fields.maintenanceKind"), render: (m) => maintenanceKindLabel(m.kind, lang) },
    { key: "status", header: t("fields.status"), render: (m) => <Badge tone={toneOf(MAINTENANCE_STATUS_TONES, m.status)}>{maintenanceStatusLabel(m.status, lang)}</Badge> },
    { key: "opened", header: t("fields.openedAt"), render: (m) => formatDate(m.openedAt, lang) },
    { key: "closed", header: t("fields.closedAt"), hideOnMobile: true, render: (m) => formatDate(m.closedAt, lang) },
    { key: "workshop", header: t("fields.workshop"), hideOnMobile: true, render: (m) => supplierName(m.supplierId) },
    ...(canSeeCosts ? [{ key: "cost", header: t("fields.cost"), align: "right" as const, render: (m: MaintenanceRow) => formatMoney(m.cost, m.currency, lang) }] : []),
    {
      key: "actions",
      header: "",
      align: "right",
      render: (m) =>
        m.status === "abierto" ? (
          <Button size="sm" variant="successSoft" onClick={() => setClosing(m)} data-testid="maintenance-close">
            {t("maintenance.close")}
          </Button>
        ) : null,
    },
  ];

  return (
    <SectionFrame
      title={a.name}
      subtitle={[categoryLabel(a.category, lang), a.serial].filter(Boolean).join(" · ")}
      actions={
        <div className="flex flex-wrap items-center gap-2" data-testid="asset-actions">
          {back}
          {can("editar") ? (
            <Button size="sm" variant="secondary" icon={<i className="ri-edit-2-line" />} onClick={() => setModal("edit")} data-testid="action-edit">
              {t("detail.edit")}
            </Button>
          ) : null}
          {can("asignar") ? (
            <Button size="sm" icon={<i className="ri-user-shared-line" />} onClick={() => setModal("assign")} data-testid="action-assign">
              {t("detail.assign")}
            </Button>
          ) : null}
          {can("devolver") ? (
            <Button size="sm" variant="secondary" icon={<i className="ri-arrow-go-back-line" />} loading={giveBack.isPending} onClick={() => void onReturn()} data-testid="action-return">
              {t("assign.return")}
            </Button>
          ) : null}
          {can("abrir_mantenimiento") ? (
            <Button size="sm" variant="warningSoft" icon={<i className="ri-tools-line" />} onClick={() => setModal("maintenance")} data-testid="action-maintenance">
              {t("detail.maintenance")}
            </Button>
          ) : null}
          {can("cerrar_mantenimiento") && openMaintenance ? (
            <Button size="sm" variant="successSoft" icon={<i className="ri-checkbox-circle-line" />} onClick={() => setClosing(openMaintenance)} data-testid="action-close-maintenance">
              {t("maintenance.close")}
            </Button>
          ) : null}
          {can("baja") ? (
            <Button size="sm" variant="dangerSoft" icon={<i className="ri-forbid-line" />} onClick={() => setModal("retire")} data-testid="action-retire">
              {t("detail.retire")}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2" data-testid="asset-status">
        <Badge tone={toneOf(ASSET_STATUS_TONES, status)}>{statusLabel(status, lang)}</Badge>
        <Badge tone={toneOf(WARRANTY_TONES, warranty)}>{t(`warranty.${warranty}`)}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title={t("detail.sheet")} />
          <InfoRow label={t("fields.category")}>{categoryLabel(a.category, lang)}</InfoRow>
          <InfoRow label={t("fields.brand")}>{a.brand || "—"}</InfoRow>
          <InfoRow label={t("fields.model")}>{a.model || "—"}</InfoRow>
          <InfoRow label={t("fields.serial")}>{a.serial || "—"}</InfoRow>
          <InfoRow label={t("fields.nextMaintenance")}>{formatDate(a.maintenance?.nextDueAt, lang)}</InfoRow>
          <InfoRow label={t("fields.notes")}>{a.notes || "—"}</InfoRow>
        </Card>

        <Card>
          <CardHeader title={t("detail.purchase")} />
          <InfoRow label={t("fields.supplier")}>{supplierName(p?.supplierId)}</InfoRow>
          <InfoRow label={t("fields.purchaseDate")}>{formatDate(p?.date, lang)}</InfoRow>
          <InfoRow label={t("fields.invoice")}>
            {[p?.invoice?.serie, p?.invoice?.folio].filter(Boolean).join("-") || p?.invoice?.uuid || "—"}
          </InfoRow>
          <InfoRow label={t("fields.warrantyUntil")}>{formatDate(a.warranty?.until, lang)}</InfoRow>
          {canSeeCosts ? (
            <div data-testid="asset-costs">
              <InfoRow label={t("fields.unitCost")}>{formatMoney(p?.unitCost, p?.currency, lang)}</InfoRow>
              <InfoRow label={t("detail.bookValue")}>
                {a.computed?.bookValue ? formatMoney(a.computed.bookValue.bookValue, BASE_CURRENCY, lang) : "—"}
              </InfoRow>
              {a.computed?.bookValue ? (
                <p className="pt-1 text-xs text-content-faint">
                  {t("detail.bookValueHint", { months: a.computed.bookValue.monthsDepreciated, life: a.computed.bookValue.usefulLifeMonths })}
                </p>
              ) : null}
            </div>
          ) : null}
        </Card>

        <Card>
          <CardHeader title={t("detail.assignment")} />
          {hasAssignment(a.assignment) ? (
            <>
              <InfoRow label={t("fields.custodian")}>{a.assignment.custodian?.name || a.assignment.custodian?.email || "—"}</InfoRow>
              <InfoRow label={t("fields.vehicle")}>{vehicleName || "—"}</InfoRow>
              <InfoRow label={t("fields.base")}>{baseName || "—"}</InfoRow>
              <InfoRow label={t("fields.location")}>{a.assignment.location || "—"}</InfoRow>
              <InfoRow label={t("fields.since")}>{formatDate(a.assignment.since, lang)}</InfoRow>
            </>
          ) : (
            <p className="py-2 text-sm text-content-muted">{status === "baja" ? t("detail.retiredNoAssignment") : t("detail.inStock")}</p>
          )}
        </Card>

        {status === "baja" ? (
          <Card>
            <CardHeader title={t("detail.retirement")} />
            <InfoRow label={t("fields.retireReason")}>{retireReasonLabel(a.retiredReason, lang)}</InfoRow>
            <InfoRow label={t("fields.retiredAt")}>{formatDate(a.retiredAt, lang)}</InfoRow>
            <InfoRow label={t("fields.note")}>{a.retiredNote || "—"}</InfoRow>
            <p className="pt-1 text-xs text-content-faint">{t("detail.retiredHint")}</p>
          </Card>
        ) : null}

        <AttachmentsPanel target={{ assetId: a.id }} attachments={a.attachments} />
      </div>

      <Card>
        <CardHeader title={t("detail.maintenances")} subtitle={t("detail.maintenancesHint")} />
        {maintenance.isLoading ? (
          <Loader />
        ) : maintenances.length === 0 ? (
          <p className="py-2 text-sm text-content-muted">{t("detail.noMaintenance")}</p>
        ) : (
          <Table columns={maintenanceColumns} rows={maintenances} rowKey={(m) => m.id} compact />
        )}
      </Card>

      <Card>
        <CardHeader title={t("detail.timeline")} />
        {events.isLoading ? (
          <Loader />
        ) : timeline.length === 0 ? (
          <EmptyState icon="ri-time-line" title={t("detail.noEvents")} />
        ) : (
          <ol className="space-y-3" data-testid="asset-timeline">
            {timeline.map((ev) => {
              const detail = eventDetail(ev, lang, t);
              return (
                <li key={ev.id} className="flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-subtle text-content-secondary">
                    <i className={(ASSET_EVENT_ICONS as Record<string, string>)[ev.type] || "ri-information-line"} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content">{eventLabel(ev.type, lang)}</p>
                    {detail ? <p className="text-xs text-content-secondary">{detail}</p> : null}
                    <p className="text-xs text-content-faint">{formatDateTime(ev.at, lang)}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {events.hasNextPage ? (
          <div className="flex justify-center pt-3">
            <Button size="sm" variant="secondary" onClick={() => void events.fetchNextPage()} disabled={events.isFetchingNextPage}>
              {events.isFetchingNextPage ? <InlineSpinner /> : t("common.loadMore")}
            </Button>
          </div>
        ) : null}
      </Card>

      {modal === "edit" ? <AssetFormModal asset={a} canSeeCosts={canSeeCosts} onClose={() => setModal(null)} /> : null}
      {modal === "assign" ? <AssignModal asset={a} onClose={() => setModal(null)} /> : null}
      {modal === "maintenance" ? <OpenMaintenanceModal asset={a} canSeeCosts={canSeeCosts} onClose={() => setModal(null)} /> : null}
      {modal === "retire" ? <RetireModal asset={a} onClose={() => setModal(null)} /> : null}
      {closing ? <CloseMaintenanceModal maintenance={closing} canSeeCosts={canSeeCosts} onClose={() => setClosing(null)} /> : null}
    </SectionFrame>
  );
}
